/*
  customsirge_sticky_atc.js
  Purpose:  Behaviour for sections/customsirge_sticky_atc.liquid — the fixed
            bottom sticky add-to-cart bar. Wires this section's own quantity
            input + submit button to pdp_main's REAL product form (this
            section owns no <form> of its own), mirrors
            `customsirge:variant-change` (dispatched by
            assets/customsirge_pdp_main.js on its section root, detail
            `{variant, product}` — see that file's own registry row), and
            shows/hides the bar via IntersectionObserver on the buy-box ATC.
  Spec:     .claude/knowledge/extraction/figma/sections/sticky_atc/spec.json
            "dawn_mechanics" + "motion" + BRIEF.md "## Client answers".
  Data:     none ambient — reads data-* attributes and DOM state only.
  Owner:    figma-builder (sticky_atc) — 2026-09-15
  Updated:  figma-fixer(sticky_atc) loop 1, 2026-09-15 — STK-01, STK-02,
            STK-06, STK-07, STK-08, STK-10, STK-12 (see .claude/knowledge/
            runs/2026-09-15/sticky_atc/discrepancies.json).
            v1.1, 2026-09-15 — B-24 (customsirge: decision 0055 §3):
            `[data-customsirge-sticky-atc-price]` moved from a standalone
            node beside the title into the ATC button's own label
            (`.customsirge-sticky-atc__btn-price`, nested in `.__btn-label`).
            No JS change needed — initPriceSync below only ever
            queries/writes that data attribute's element, wherever it lives.
            v1.1, 2026-09-15 — B-22 §4 (pdp_main's viewport freeze, decision
            0055 §1 follow-up — see runs/2026-09-15/v1_1/pdp_main/NOTES.md
            "Sticky-ATC trigger" finding): this bar's own trigger
            (IntersectionObserver on `.customsirge-pdp-main__ctas`) was
            correct on its own terms but blind to pdp_main's viewport
            freeze — a bar already shown before the freeze engaged stayed
            shown, overlapping the frozen section's bottom edge for the
            whole freeze, because nothing about the CTAs' OWN intersection
            changes while the whole viewport (CTAs included) holds still.
            initVisibility now also listens for `customsirge:pdp-pin-change`
            (dispatched by assets/customsirge_pdp_main.js's initGallerySwap)
            and ANDs "not currently pinned" into the visibility decision —
            same shape as the existing "CTAs out of view" condition, just a
            second, independent signal this section has no other way to
            observe from a different section's DOM.

  customsirge: WHY NOT DAWN'S <quantity-input> — see the section file's own
  header, gotcha 3 (traced `validateQtyRules()` hardcoding
  `.quantity__button[name=...]`). This file's stepper is a small, self-
  contained equivalent scoped to this bar's own BEM classes.

  customsirge: WHY THE FORM IS WIRED HERE, NOT IN LIQUID — pdp_main's own
  `form_id` (`'customsirge-pdp-form-' | append: section.id`) belongs to
  pdp_main's section instance; this section cannot read that id at Liquid-
  render time. Wiring happens once, at init, by finding pdp_main's real
  `form[data-type="add-to-cart-form"]` (rendered by Dawn's `{% form %}` tag)
  and copying its id onto this bar's own quantity input + submit button via
  the `form=""` attribute — the same "form" association Shopify's own docs
  use for a control living outside its target <form>.
*/
(function () {
  'use strict';

  function clampQty(input) {
    var min = parseInt(input.min, 10);
    if (isNaN(min)) min = 1;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < min) value = min;
    if (input.max) {
      var max = parseInt(input.max, 10);
      if (!isNaN(max) && value > max) value = max;
    }
    input.value = value;
    return value;
  }

  function initStepper(root, onQtyChange) {
    var group = root.querySelector('[data-customsirge-sticky-atc-qty]');
    var minus = root.querySelector('[data-customsirge-sticky-atc-qty-minus]');
    var plus = root.querySelector('[data-customsirge-sticky-atc-qty-plus]');
    var input = root.querySelector('[data-customsirge-sticky-atc-qty-input]');
    if (!group || !minus || !plus || !input) return null;

    // STK-12: sold out disables the whole stepper (spec
    // states_undrawn_proposed_defaults.sold_out); seeded from the SSR-
    // rendered `disabled` attribute (Liquid sets it when
    // current_variant.available is false) so init doesn't clobber the
    // sold-out-at-load state, then tracked separately from the min/max-
    // driven disabling below so re-enabling on variant-change doesn't have
    // to guess at the qty value.
    var available = !input.disabled;

    function refreshDisabled() {
      var value = parseInt(input.value, 10);
      var min = parseInt(input.min, 10);
      minus.disabled = !available || (!isNaN(min) && value <= min);
      var maxHit = false;
      if (input.max) {
        var max = parseInt(input.max, 10);
        maxHit = !isNaN(max) && value >= max;
      }
      plus.disabled = !available || maxHit;
      input.disabled = !available;
    }

    function stepBy(direction) {
      var stepSize = parseInt(input.step, 10) || 1;
      var value = parseInt(input.value, 10) || 1;
      input.value = value + stepSize * direction;
      clampQty(input);
      refreshDisabled();
      onQtyChange();
    }

    function onMinus(event) { event.preventDefault(); stepBy(-1); }
    function onPlus(event) { event.preventDefault(); stepBy(1); }
    function onInput() { clampQty(input); refreshDisabled(); onQtyChange(); }

    minus.addEventListener('click', onMinus);
    plus.addEventListener('click', onPlus);
    input.addEventListener('change', onInput);
    refreshDisabled();

    return {
      cleanup: function () {
        minus.removeEventListener('click', onMinus);
        plus.removeEventListener('click', onPlus);
        input.removeEventListener('change', onInput);
      },
      setAvailable: function (isAvailable) {
        available = !!isAvailable;
        refreshDisabled();
      },
    };
  }

  function initFormWiring(root) {
    var input = root.querySelector('[data-customsirge-sticky-atc-qty-input]');
    var submit = root.querySelector('[data-customsirge-sticky-atc-submit]');
    var spinner = submit && submit.querySelector('.customsirge-sticky-atc__spinner');

    function wire() {
      var form = document.querySelector('form[data-type="add-to-cart-form"]');
      // STK-01: `form.id` is shadowed by Dawn's own <input type="hidden"
      // name="id"> inside every {% form 'product' %} — a form with a control
      // named "id" makes the `id` PROPERTY resolve to that control, not the
      // form's id string. getAttribute('id') always reads the attribute.
      var formId = form && form.getAttribute('id');
      if (!form || !formId) return false;
      if (input) input.setAttribute('form', formId);
      if (submit) submit.setAttribute('form', formId);
      return true;
    }

    wire();

    // STK-06: a product that loads sold out has no buy-box <form> yet (Dawn
    // only renders it for an available variant); when the shopper switches
    // to an available variant, pdp_main renders one for the first time —
    // re-run the wiring so the sticky ATC/quantity join it instead of
    // staying permanently unassociated from a single init-time call.
    function onVariantChange() { wire(); }
    document.addEventListener('customsirge:variant-change', onVariantChange);

    var teardownClick;
    if (submit && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      var onClick = function () {
        // STK-10: 'adding' state — aria-busy + a spinner, label kept (spec
        // states_undrawn_proposed_defaults.adding).
        submit.setAttribute('aria-disabled', 'true');
        submit.setAttribute('aria-busy', 'true');
        submit.classList.add('is-loading');
        if (spinner) spinner.classList.remove('hidden');
      };
      var clear = function () {
        submit.removeAttribute('aria-disabled');
        submit.removeAttribute('aria-busy');
        submit.classList.remove('is-loading');
        if (spinner) spinner.classList.add('hidden');
      };
      submit.addEventListener('click', onClick);
      var unsubUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, clear);
      var unsubError = subscribe(PUB_SUB_EVENTS.cartError, clear);
      teardownClick = function () {
        submit.removeEventListener('click', onClick);
        if (typeof unsubUpdate === 'function') unsubUpdate();
        if (typeof unsubError === 'function') unsubError();
      };
    }

    return function cleanup() {
      document.removeEventListener('customsirge:variant-change', onVariantChange);
      if (typeof teardownClick === 'function') teardownClick();
    };
  }

  function activePlanId() {
    var checked = document.querySelector('[data-customsirge-plan-input]:checked');
    return checked ? checked.value : '';
  }

  function initPriceSync(root, getQty) {
    var priceEl = root.querySelector('[data-customsirge-sticky-atc-price]');
    if (!priceEl || !window.Customsirge || typeof Customsirge.formatMoney !== 'function') {
      return { update: function () {}, setCents: function () {}, cleanup: function () {} };
    }

    function update() {
      var plan = activePlanId();
      var onetimeCents = parseInt(priceEl.getAttribute('data-price-onetime'), 10) || 0;
      var subscribeCents = parseInt(priceEl.getAttribute('data-price-subscribe'), 10) || onetimeCents;
      var selectedOption = plan ? document.querySelector('[data-customsirge-freq-opt="' + plan + '"]') : null;
      if (selectedOption && selectedOption.hasAttribute('data-gigli-plan-cents')) {
        var selectedCents = Number(selectedOption.getAttribute('data-gigli-plan-cents'));
        if (Number.isFinite(selectedCents) && selectedCents >= 0) subscribeCents = selectedCents;
      }
      var unit = plan ? subscribeCents : onetimeCents;
      priceEl.textContent = Customsirge.formatMoney(unit * getQty());
    }

    function setCents(onetimeCents, subscribeCents) {
      priceEl.setAttribute('data-price-onetime', onetimeCents);
      priceEl.setAttribute('data-price-subscribe', subscribeCents != null ? subscribeCents : onetimeCents);
      update();
    }

    // STK-08: this was never removed, so every theme-editor section:load
    // re-init added another 'change' listener on `document`. Keep the
    // handler reference and remove it from the returned cleanup, like every
    // other init function here does.
    function onDocumentChange(event) {
      if (event.target && event.target.matches && event.target.matches('[data-customsirge-plan-input]')) update();
    }
    document.addEventListener('change', onDocumentChange);

    return {
      update: update,
      setCents: setCents,
      cleanup: function () { document.removeEventListener('change', onDocumentChange); },
    };
  }

  function initVariantMirror(root, priceSync, refreshQty, stepper) {
    var thumbWrap = root.querySelector('[data-customsirge-sticky-atc-thumb]');
    var thumbImg = root.querySelector('.customsirge-sticky-atc__thumb img');
    var submit = root.querySelector('[data-customsirge-sticky-atc-submit]');
    var notifyEl = root.querySelector('[data-customsirge-notify]');

    function onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      // STK-07: a variant without its own featured_image used to keep
      // whatever the PREVIOUS variant left in `src`. Fall back to the SSR-
      // stashed product.featured_image (spec editable[0]).
      var nextSrc = (variant.featured_image && variant.featured_image.src) ||
        (thumbWrap && thumbWrap.getAttribute('data-fallback-src'));
      if (thumbImg && nextSrc) {
        thumbImg.src = nextSrc;
        if (thumbImg.srcset) thumbImg.removeAttribute('srcset');
      }

      var subscribeCents = null;
      if (variant.selling_plan_allocations && variant.selling_plan_allocations.length) {
        subscribeCents = variant.selling_plan_allocations[0].price;
      }
      priceSync.setCents(variant.price, subscribeCents);

      if (submit) submit.hidden = !variant.available;
      // qtyGroup visibility is owned by section.settings.show_quantity + the <750px CSS rule —
      // availability does not touch it (the input stays in the DOM either way, per spec).
      // STK-12: stepper disabled when sold out (spec
      // states_undrawn_proposed_defaults.sold_out), kept in sync as
      // availability flips after load.
      if (stepper) stepper.setAvailable(variant.available);
      if (notifyEl) {
        notifyEl.hidden = !!variant.available;
        notifyEl.setAttribute('data-variant-id', variant.id);
      }
      refreshQty();
    }

    document.addEventListener('customsirge:variant-change', onVariantChange);
    return function cleanup() {
      document.removeEventListener('customsirge:variant-change', onVariantChange);
    };
  }

  function initVisibility(root) {
    // STK-02: pdp_main's variant fetch does
    // `#customsirge-pdp-ctas-<id>`.innerHTML = next.innerHTML on every
    // variant change — that DESTROYS whichever descendant node (the real
    // ATC button, or its sold-out Notify-me swap) this observer was
    // watching; the detached node stays isIntersecting=false forever, so
    // the bar got stuck permanently visible after the first variant change.
    // `.customsirge-pdp-main__ctas` is the STABLE wrapper around both
    // branches — pdp_main only ever replaces ITS innerHTML, never the div
    // itself (patterns/sticky-atc.md #5) — so observing it survives every
    // variant change with no re-resolution needed.
    var trigger = document.querySelector('.customsirge-pdp-main__ctas');
    if (!trigger || !('IntersectionObserver' in window)) return undefined;

    // B-22 §4: the bar may only show when BOTH are true — the buy-box CTAs
    // are out of view (this section's own original trigger) AND pdp_main is
    // not currently mid-freeze (customsirge:pdp-pin-change, a signal from a
    // different section — see this file's header). Either flag flipping
    // must re-run the decision independently, since the freeze can engage/
    // release without the CTAs' own intersection changing at all.
    var ctasHidden = false;
    var pinned = false;

    function setVisible(visible) {
      root.classList.toggle('is-visible', visible);
      root.setAttribute('aria-hidden', String(!visible));
      if ('inert' in root) root.inert = !visible;
      document.body.style.paddingBottom = visible ? root.getBoundingClientRect().height + 'px' : '';
    }

    function refresh() {
      setVisible(ctasHidden && !pinned);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        ctasHidden = !entry.isIntersecting;
        refresh();
      },
      { threshold: 0 }
    );
    observer.observe(trigger);

    function onPinChange(event) {
      pinned = !!(event.detail && event.detail.pinned);
      refresh();
    }
    document.addEventListener('customsirge:pdp-pin-change', onPinChange);

    return function cleanup() {
      observer.disconnect();
      document.removeEventListener('customsirge:pdp-pin-change', onPinChange);
      document.body.style.paddingBottom = '';
    };
  }

  Customsirge.register('sticky_atc', {
    selector: '[data-customsirge-sticky-atc]',
    init: function (root) {
      var qtyInput = root.querySelector('[data-customsirge-sticky-atc-qty-input]');
      var priceSync = initPriceSync(root, function () {
        return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      });
      var stepper = initStepper(root, priceSync.update);
      var formTeardown = initFormWiring(root);
      var variantTeardown = initVariantMirror(root, priceSync, function () {
        if (qtyInput) clampQty(qtyInput);
      }, stepper);
      var visibilityTeardown = initVisibility(root);

      return function cleanup() {
        [stepper && stepper.cleanup, formTeardown, variantTeardown, visibilityTeardown, priceSync.cleanup].forEach(function (fn) {
          if (typeof fn === 'function') fn();
        });
      };
    },
  });
})();
